import React, { useState } from 'react';
import { RefreshCw, Download, Mail, Plus, Trash2 } from 'lucide-react';

const SubscribersTab = ({ subscribers, onRefresh, onExportCSV, password }) => {
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const addSubscriber = async () => {
    if (!newEmail.trim() || busy) return;
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, email: newEmail.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setMsg(`${data.email} added ✓`);
        setNewEmail('');
        onRefresh();
      } else {
        setError(data.detail || 'Failed to add');
      }
    } catch {
      setError('Failed to add');
    }
    setBusy(false);
  };

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Remove ${email} from the newsletter list?`)) return;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/subscribers?password=${encodeURIComponent(password)}&email=${encodeURIComponent(email)}`, { method: 'DELETE' });
      onRefresh();
    } catch { /* refresh shows state */ }
  };

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Newsletter Subscribers</h1>
        <div className="flex space-x-3">
          <button onClick={onRefresh}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
            data-testid="refresh-subscribers-button">
            <RefreshCw className="w-4 h-4" /><span className="text-sm">Refresh</span>
          </button>
          {subscribers.length > 0 && (
            <button onClick={onExportCSV}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              data-testid="export-subscribers-button">
              <Download className="w-4 h-4" /><span className="text-sm">Export CSV</span>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6 p-4 rounded-2xl border border-border bg-muted/30">
        <input
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addSubscriber(); }}
          placeholder="Add an email to the list..."
          className="flex-1 min-w-[220px] px-4 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="add-subscriber-input"
        />
        <button onClick={addSubscriber} disabled={busy || !newEmail.trim()}
          className="inline-flex items-center space-x-1.5 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40"
          data-testid="add-subscriber-button">
          <Plus className="w-4 h-4" /><span>{busy ? 'Adding...' : 'Add Subscriber'}</span>
        </button>
        {msg && <span className="text-green-600 text-sm font-medium" data-testid="add-subscriber-success">{msg}</span>}
        {error && <span className="text-red-500 text-sm" data-testid="add-subscriber-error">{error}</span>}
      </div>

      {subscribers.length === 0 ? (
        <div className="text-center py-16">
          <Mail className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-foreground/70">No subscribers yet</p>
          <p className="text-sm text-foreground/50 mt-1">Add emails above, or they join via the website footer signup</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/30">
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">#</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Email</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-foreground">Subscribed</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {subscribers.map((sub, index) => (
                <tr key={sub.email} className="border-t border-border hover:bg-muted/10 transition-colors" data-testid={`subscriber-row-${index}`}>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{index + 1}</td>
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{sub.email}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {new Date(sub.subscribed_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => removeSubscriber(sub.email)} className="p-1.5 text-red-400 hover:text-red-600 transition-colors" data-testid={`remove-subscriber-${index}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default SubscribersTab;
